/*
  Renders one list row inside the FileExplorer.

  Uniform item shape:
    { raw, title, subtitle?, meta? }
  `raw` is the original S3/DDB string (passed through click handlers untouched).
  `title` / `subtitle` are the friendly display strings.

  `level` selects which action buttons to render:
    experiments | samples | snapshots | analyses | images
*/

import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import MovieIcon from '@mui/icons-material/Movie';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { v4 as uuidv4 } from 'uuid';

import { allocateSession } from '../utils/apiClient';

function LeadIcon({ level }) {
  const color = '#7c3bed';
  if (level === 'images') return <InsertPhotoOutlinedIcon sx={{ color: '#888', mr: 1 }} fontSize="small" />;
  if (level === 'snapshots') return <HistoryIcon sx={{ color, mr: 1 }} fontSize="small" />;
  if (level === 'analyses') return <AssessmentIcon sx={{ color, mr: 1 }} fontSize="small" />;
  if (level === 'samples') return <PhotoLibraryIcon sx={{ color, mr: 1 }} fontSize="small" />;
  return <FolderIcon sx={{ color, mr: 1 }} fontSize="small" />;
}

const FileList = ({
  items,
  level,
  onItemClick,
  // image-row actions
  onDeleteImage,
  onDownloadImage,
  onAnalyzeImage,
  // sample-row actions
  onDownloadSample,
  onCreateMp4,
  onAnalyzeSample,
  // analysis-row actions
  onOpenAnalysis,
  onDeleteAnalysis,
}) => {
  const [busyAnalyze, setBusyAnalyze] = useState(false);

  const startAnalyze = async (sampleRaw) => {
    if (!sampleRaw) return;
    setBusyAnalyze(true);
    const sessionId = uuidv4();
    sessionStorage.setItem('sessionId', sessionId);
    const selectedDevice = JSON.parse(localStorage.getItem('selectedDevice'));
    if (!selectedDevice) {
      alert('No device selected. Please select a device before starting analysis.');
      setBusyAnalyze(false);
      return;
    }
    sessionStorage.setItem('device_name', selectedDevice.device_name);
    sessionStorage.setItem('device_bucket', selectedDevice.device_bucket);
    try {
      await allocateSession(sessionId, selectedDevice.device_bucket, selectedDevice.device_name);
      onAnalyzeSample?.(sampleRaw, { sessionId, isStarting: true });
    } catch (err) {
      console.error('Error starting analysis session:', err);
      alert('Unable to start analysis session. Please try again.');
    } finally {
      setBusyAnalyze(false);
    }
  };

  return (
    <div className="file-component" style={{ position: 'relative' }}>
      {items.map((item, idx) => {
        const raw = item.raw;
        const title = item.title || raw;
        const subtitle = item.subtitle;
        const tooltip = raw && raw !== title ? raw : '';
        return (
          <div
            key={`${raw}-${idx}`}
            className="file"
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              marginBottom: 6,
              padding: '8px 10px',
              borderRadius: 6,
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(124, 59, 237, 0.12)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <LeadIcon level={level} />
            <Tooltip title={tooltip} placement="top-start" arrow disableHoverListener={!tooltip}>
              <div
                style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden' }}
                onClick={() => onItemClick?.(raw, item)}
              >
                <div
                  style={{
                    color: '#fff',
                    fontSize: '0.92rem',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {title}
                </div>
                {subtitle && (
                  <div style={{ color: '#888', fontSize: '0.75rem', marginTop: 2 }}>
                    {subtitle}
                  </div>
                )}
              </div>
            </Tooltip>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
              {level === 'images' && (
                <>
                  <Tooltip title="Delete"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onDeleteImage?.(raw)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title="Download image"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onDownloadImage?.(raw)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title="Analyze this image"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onAnalyzeImage?.(raw)}>
                      <AnalyticsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                </>
              )}
              {level === 'samples' && (
                <>
                  <Tooltip title="Download raw images"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onDownloadSample?.(raw)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title="Download movie"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onCreateMp4?.(raw)}>
                      <MovieIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title="Analyze"><span>
                    <IconButton
                      size="small"
                      sx={{ color: '#bbb' }}
                      disabled={busyAnalyze}
                      onClick={() => startAnalyze(raw)}
                    >
                      {busyAnalyze ? <CircularProgress size={16} /> : <AnalyticsOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </span></Tooltip>
                </>
              )}
              {level === 'analyses' && (
                <>
                  <Tooltip title="Open analysis"><span>
                    <IconButton size="small" sx={{ color: '#bbb' }} onClick={() => onOpenAnalysis?.(raw, item)}>
                      <AnalyticsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                  <Tooltip title="Delete analysis"><span>
                    <IconButton
                      size="small"
                      sx={{ color: '#bbb' }}
                      onClick={() => {
                        if (window.confirm(`Delete analysis "${title}"?`)) onDeleteAnalysis?.(raw);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span></Tooltip>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FileList;
